import { ROUTES } from '@constants';
import { useApplicationStore } from '@data';
import { Events, tracking } from '@lib';
import { buildLeadSessionId } from '@utils';
import { LoaderFunctionArgs, redirect } from 'react-router-dom';
import {
  getStateForCurrentLocation,
  loadServiceTypesByState,
  parseUtmParametersFromSearch,
} from './utils';
import { getVisitTypeUrl } from '@router/utils';
import { initializeMixpanel } from '@lib/session-replay';
import { getIntakeQuizRedirectPath } from '@lib/experiments/matQuestions';

export default async function rootLoader({ request }: LoaderFunctionArgs) {
  initializeMixpanel(tracking.analytics);
  const url = new URL(request.url);
  const utmParameters = parseUtmParametersFromSearch(url.search);
  const {
    auth,
    getVisitTypeById,
    patient,
    setLeadSessionId,
    setShouldSearchNextAvailability,
    setStateCode,
    setUtmParameters,
  } = useApplicationStore.getState();
  auth.clearAuthData();
  patient.clearPatientProfile();
  const leadSessionId = buildLeadSessionId();
  tracking.trackEvent(Events.ONBOARDING_STARTED, {
    ...utmParameters,
    fbpixel: undefined,
  });

  setLeadSessionId(leadSessionId);
  setUtmParameters(utmParameters);

  const stateCode = await getStateForCurrentLocation();

  if (!stateCode) {
    return redirect(ROUTES.services.path);
  }

  if (stateCode) {
    tracking.trackEvent(Events.SELECT_STATE, { AutoSelected: true });

    setStateCode(stateCode);
  }

  if (!utmParameters.service) {
    const servicesUrl = `${ROUTES.services.path}?state=${stateCode}`;
    return redirect(servicesUrl);
  }

  if (!stateCode) {
    return redirect(ROUTES.services.path);
  }

  // NOTE:
  // The scheduling page relies on services being in the store to look up the service name.
  await loadServiceTypesByState(stateCode);

  const isVisitIdFound = getVisitTypeById(utmParameters.service);

  if (!isVisitIdFound) {
    // FIXME: Track an event and/or error
    return redirect(`${ROUTES.services.path}${stateCode ? `?state=${stateCode}` : ''}`);
  }

  setShouldSearchNextAvailability(true);

  // Treatment-arm MAT patients arriving via `?utm_service=<addiction-VT>`
  // would otherwise land on /schedule and bypass the intake quiz entirely
  // (firing Experiment Started from scheduleLoader without ever viewing a
  // question). Route them to the quiz instead so the cohort sees the
  // experiment they were enrolled in.
  const intakeQuizPath = getIntakeQuizRedirectPath(utmParameters.service);
  if (intakeQuizPath) {
    return redirect(intakeQuizPath);
  }

  return redirect(getVisitTypeUrl(utmParameters.service));
}
